import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { GeneratedContent } from "@/types/content";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useState, useCallback, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { CalendarScheduler } from "@/components/ui/calendar-scheduler";
import { 
  Clock, 
  Calendar, 
  CheckCircle, 
  AlertCircle, 
  Plus,
  Filter,
  Eye,
  Edit,
  Trash2,
  Send,
  Bot,
  UserRound,
  Target,
  BarChart3,
  ThumbsUp,
  MessageCircle,
  ExternalLink,
  RefreshCw,
  Search,
  Settings,
  X,
  MessageSquare,
  Share,
  Sparkles,
  Clipboard,
  ClipboardCheck
} from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { scheduleRedditPost, getScheduledPosts, getApprovedSubreddits, updatePostStatus } from '@/services/reddit';
import { formatDistance } from "date-fns";
import { useLocation } from "react-router-dom";
import { format, startOfDay, addDays, isToday, isTomorrow, isThisWeek, parseISO, isSameDay } from "date-fns";

// Constants
const MAX_CONTENT_PREVIEW = 300;
const REFETCH_INTERVAL_SCHEDULED = 30000; // 30 seconds
const REFETCH_INTERVAL_GENERATED = false; // Only refetch when generating
const GENERATION_TIMEOUT = 30000; // 30 seconds
const RECENT_POST_WINDOW = 5 * 60 * 1000; // 5 minutes
const QUERY_INVALIDATION_DELAY = 100; // 100ms

// Status constants
const POST_STATUS = {
  SENT: 'sent',
  APPROVED_FOR_PUBLISHING: 'approved_for_publishing',
  PUBLISHING: 'publishing',
  QUEUED_BY_AGENT: 'queued_by_agent',
  AWAITING_APPROVAL: 'awaiting_approval',
  ERROR_POSTING: 'error_posting',
  CANCELLED: 'cancelled'
} as const;

// Union type for post statuses
type PostStatus = typeof POST_STATUS[keyof typeof POST_STATUS];

const ACTIVE_STATUSES: PostStatus[] = [
  POST_STATUS.APPROVED_FOR_PUBLISHING, 
  POST_STATUS.QUEUED_BY_AGENT, 
  POST_STATUS.PUBLISHING
];

const COMPLETED_STATUSES: PostStatus[] = [POST_STATUS.SENT];
const FAILED_STATUSES: PostStatus[] = [POST_STATUS.ERROR_POSTING];

// Types for scheduled posts (based on actual database schema)
interface ScheduledRedditPost {
  id: string;
  user_id: string;
  project_id: string;
  subreddit_name: string;
  post_title: string;
  post_content: string;
  scheduled_at_utc: string;
  status: string;
  reddit_post_id?: string;
  error_message?: string;
  preferred_flair?: string;
  created_at: string;
  updated_at: string;
  retry_count?: number;
}

const RedditContentManager = () => {
  const { id: projectId } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();

  // State management
  const [selectedTab, setSelectedTab] = useState('generated');
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedPost, setSelectedPost] = useState<GeneratedContent | null>(null);
  const [isScheduling, setIsScheduling] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [copied, setCopied] = useState<Record<string, boolean>>({});
  const [activeSubreddit, setActiveSubreddit] = useState<string | null>(null);
  const [scheduledStatusFilter, setScheduledStatusFilter] = useState<string>('all');
  const [scheduleForm, setScheduleForm] = useState({
    subreddit: '',
    scheduledAt: '',
    customTitle: '',
    customContent: ''
  });

  // Generation state (for real-time updates)
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isWaitingForContent, setIsWaitingForContent] = useState(false);
  const [generationTimestamp, setGenerationTimestamp] = useState<number | null>(null);
  const [generationId, setGenerationId] = useState<string | null>(null);

  // Add debounce tracking for cancel operations
  const [lastCancelTime, setLastCancelTime] = useState<Record<string, number>>({});
  
  // Flair retry state
  const [flairRetryDialogOpen, setFlairRetryDialogOpen] = useState(false);
  const [retryingPostId, setRetryingPostId] = useState<string | null>(null);
  const [selectedFlairForRetry, setSelectedFlairForRetry] = useState('');

  // Check for generation in progress on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const isGeneratingFromUrl = urlParams.get("reddit_generating") === "true";
    
    if (isGeneratingFromUrl) {
      setIsGenerating(true);
      setGenerationTimestamp(Date.now());
      
      // Start progress simulation
      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          const newProgress = Math.min(prev + Math.random() * 15, 95);
          return newProgress;
        });
      }, 1000);

      // Check for new content every 2 seconds
      const contentCheckInterval = setInterval(() => {
        try {
          queryClient.invalidateQueries({ queryKey: ['generated-content'] });
        } catch (error) {
          console.error('Error refreshing content during generation:', error);
          toast({
            title: "Background refresh failed",
            description: "Content generation is continuing, but auto-refresh encountered an issue.",
            variant: "default"
          });
        }
      }, 2000);

      // Cleanup function
      const cleanup = () => {
        clearInterval(progressInterval);
        clearInterval(contentCheckInterval);
      };

      // Auto-cleanup after 30 seconds
      setTimeout(() => {
        cleanup();
        setIsGenerating(false);
        setIsWaitingForContent(false);
      }, GENERATION_TIMEOUT);

      return cleanup;
    }
  }, [location.search, queryClient, toast]);

  // Fetch generated content (Reddit posts only)
  const { data: generatedPosts = [], isLoading: isLoadingGenerated } = useQuery({
    queryKey: ['generated-content', projectId, 'reddit'],
    queryFn: async () => {
      if (!projectId || !user?.id) return [];

      const { data, error } = await supabase
        .from('generated_content')
        .select('*')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .eq('platform', 'reddit')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Check if we were waiting for content and now have new content
      if (isGenerating && data.length > 0) {
        const newestPost = data[0];
        const postAge = new Date().getTime() - new Date(newestPost.created_at).getTime();
        
        // If post is less than 1 minute old, generation likely completed
        if (postAge < 60000) {
          setIsGenerating(false);
          setLoadingProgress(100);
          setIsWaitingForContent(false);
          
          // Clean up URL
          const urlParams = new URLSearchParams(window.location.search);
          urlParams.delete("reddit_generating");
          window.history.replaceState(
            {},
            "",
            window.location.pathname + (urlParams.toString() ? "?" + urlParams.toString() : "")
          );
          
          toast({
            title: "Reddit post generated!",
            description: "Your new post is ready for review and scheduling.",
          });
        }
      }
      
      return data as GeneratedContent[];
    },
    enabled: !!projectId && !!user?.id,
    refetchInterval: isGenerating ? 2000 : REFETCH_INTERVAL_GENERATED
  });

  // Fetch scheduled posts
  const { data: scheduledPosts = [], isLoading: isLoadingScheduled } = useQuery({
    queryKey: ['scheduled-reddit-posts', projectId, user?.id],
    queryFn: async () => {
      if (!projectId || !user?.id) return [];
      return await getScheduledPosts(projectId, user.id);
    },
    enabled: !!projectId && !!user?.id,
    refetchInterval: 60000, // Reduced to 1 minute to avoid conflicts with delete operations
    gcTime: 300000 // Keep in cache for 5 minutes
  });

  // Fetch approved subreddits
  const { data: approvedSubreddits = [], isLoading: isLoadingApproved, error: approvedSubredditsError } = useQuery({
    queryKey: ['approved-subreddits', projectId],
    queryFn: async () => {
      if (!projectId) return [];
      try {
        const data = await getApprovedSubreddits(projectId);
        return data;
      } catch (error) {
        console.error('Error fetching approved subreddits:', error);
        throw error; // Re-throw to be handled by React Query
      }
    },
    enabled: !!projectId && !!user?.id,
    retry: 1 // Only retry once to avoid excessive API calls
  });

  // Filter generated posts
  const filteredGeneratedPosts = useMemo(() => {
    let filtered = [...generatedPosts];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(post => 
        post.title?.toLowerCase().includes(query) ||
        post.content?.toLowerCase().includes(query) ||
        (post.metadata?.subreddit && post.metadata.subreddit.toLowerCase().includes(query))
      );
    }

    // Filter by status
    if (selectedFilter !== 'all') {
      filtered = filtered.filter(post => post.status === selectedFilter);
    }

    // Filter by active subreddit
    if (activeSubreddit) {
      filtered = filtered.filter(post => 
        (post.metadata?.subreddit || "unknown") === activeSubreddit
      );
    }

    return filtered;
  }, [generatedPosts, searchQuery, selectedFilter, activeSubreddit]);

  // Filter scheduled posts (exclude published ones)
  const filteredScheduledPosts = useMemo(() => {
    let filtered = scheduledPosts.filter(p => !COMPLETED_STATUSES.includes(p.status as PostStatus));

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(post => 
        post.post_title?.toLowerCase().includes(query) ||
        post.post_content?.toLowerCase().includes(query) ||
        post.subreddit_name?.toLowerCase().includes(query)
      );
    }

    // Filter by status
    if (scheduledStatusFilter !== 'all') {
      if (scheduledStatusFilter === 'active') {
        filtered = filtered.filter(post => ACTIVE_STATUSES.includes(post.status as PostStatus));
      } else if (scheduledStatusFilter === 'failed') {
        filtered = filtered.filter(post => FAILED_STATUSES.includes(post.status as PostStatus));
      } else {
        filtered = filtered.filter(post => post.status === scheduledStatusFilter);
      }
    }

    return filtered.sort((a, b) => {
      // Sort by scheduled time, with most recent first
      return new Date(b.scheduled_at_utc).getTime() - new Date(a.scheduled_at_utc).getTime();
    });
  }, [scheduledPosts, searchQuery, scheduledStatusFilter]);

  // Filter published posts
  const filteredPublishedPosts = useMemo(() => {
    let filtered = scheduledPosts.filter(p => COMPLETED_STATUSES.includes(p.status as PostStatus));

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(post => 
        post.post_title?.toLowerCase().includes(query) ||
        post.post_content?.toLowerCase().includes(query) ||
        post.subreddit_name?.toLowerCase().includes(query)
      );
    }

    return filtered.sort((a, b) => {
      // Sort by updated time (when they were published), most recent first
      return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime();
    });
  }, [scheduledPosts, searchQuery]);

  // Generate post counts by subreddit for generated posts
  const subredditCounts = useMemo(() => {
    return generatedPosts.reduce((acc, content) => {
      const subreddit = content.metadata?.subreddit || "unknown";
      acc[subreddit] = (acc[subreddit] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [generatedPosts]);

  // Handle scheduling a post
  const handleSchedulePost = async () => {
    if (!selectedPost || !projectId || !user?.id) return;

    setIsScheduling(true);
    try {
      // Fix timezone issue - properly handle the date conversion
      let scheduledDateTime: string;
      
      if (scheduleForm.scheduledAt) {
        // If using CalendarScheduler, the value is already in ISO format
        const localDate = new Date(scheduleForm.scheduledAt);
        
        // Add randomization to avoid bot detection (±5-15 minutes)
        const randomMinutes = Math.floor(Math.random() * 21) - 10; // Random between -10 and +10 minutes
        const randomizedDate = new Date(localDate.getTime() + (randomMinutes * 60 * 1000));
        
        scheduledDateTime = randomizedDate.toISOString();
        
        console.log('Original time:', scheduleForm.scheduledAt);
        console.log('Randomized time:', scheduledDateTime);
        console.log('Randomization offset:', randomMinutes, 'minutes');
      } else {
        throw new Error('No scheduled time provided');
      }

      await scheduleRedditPost({
        project_id: projectId,
        user_id: user.id,
        title: selectedPost.title || 'Reddit Post',
        content: selectedPost.content || '',
        subreddit_name: scheduleForm.subreddit,
        scheduled_for: scheduledDateTime,
      });

      toast({
        title: 'Post Scheduled Successfully! 🎉',
        description: `Your post will be published to r/${scheduleForm.subreddit} with optimized timing`,
      });

      // Reset form and close dialog
      setSelectedPost(null);
      setIsScheduleModalOpen(false);
      setScheduleForm({
        subreddit: '',
        scheduledAt: '',
        customTitle: '',
        customContent: ''
      });

      // Refetch data with correct query key
      queryClient.invalidateQueries({ queryKey: ['scheduled-reddit-posts', projectId, user.id] });
    } catch (error) {
      console.error('Error scheduling post:', error);
      toast({
        title: 'Error',
        description: 'Failed to schedule post. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsScheduling(false);
    }
  };

  // Handle canceling a scheduled post (moves back to generated)
  const handleCancelScheduledPost = async (postId: string) => {
    if (!user?.id || !projectId) {
      toast({
        title: 'Error',
        description: 'Missing user authentication or project ID.',
        variant: 'destructive',
      });
      return;
    }

    // DEBOUNCE CHECK: Prevent rapid successive calls
    const now = Date.now();
    if (lastCancelTime[postId] && now - lastCancelTime[postId] < 2000) {
      return; // Ignore if called within 2 seconds
    }
    setLastCancelTime(prev => ({ ...prev, [postId]: now }));

    // Confirm cancellation
    if (!confirm('Cancel this scheduled post? It will move back to Generated Posts where you can reschedule it.')) {
      return;
    }

    setDeletingId(postId);
    
    try {
      console.log('Starting cancellation process for post:', postId);
      
      // FRESH DATA CHECK: Get current post state from database to avoid stale data
      const { data: currentPost, error: fetchError } = await supabase
        .from('scheduled_reddit_posts')
        .select('*')
        .eq('id', postId)
        .eq('user_id', user.id)
        .single();

      if (fetchError) {
        console.error('Error fetching post:', fetchError);
        throw new Error(`Could not find post: ${fetchError.message}`);
      }
      
      if (!currentPost) {
        console.error('Post not found in database');
        throw new Error('Post not found or already cancelled');
      }

      console.log('Found post to cancel:', currentPost);

      // VALIDATION: Check required fields
      if (!currentPost.post_title?.trim() || !currentPost.post_content?.trim()) {
        console.error('Post missing required fields:', {
          hasTitle: !!currentPost.post_title?.trim(),
          hasContent: !!currentPost.post_content?.trim()
        });
        throw new Error('Post missing required title or content');
      }

      // OPTIMISTIC UPDATE: Remove from scheduled posts immediately
      queryClient.setQueryData(['scheduled-reddit-posts', projectId, user?.id], (oldData: any) => {
        if (!oldData) return oldData;
        return oldData.filter((post: any) => post.id !== postId);
      });

      // Create new generated content from the scheduled post
      const insertData = {
        user_id: user.id,
        project_id: projectId,
        platform: 'reddit',
        title: currentPost.post_title,
        content: currentPost.post_content,
        status: 'completed',
        metadata: {
          subreddit: currentPost.subreddit_name || 'unknown',
          originallyScheduledFor: currentPost.scheduled_at_utc,
          cancelledFromSchedule: true,
          originalScheduledPostId: postId
        },
        created_at: new Date().toISOString()
      };

      console.log('Inserting generated content:', insertData);

      const { data: insertedPost, error: insertError } = await supabase
        .from('generated_content')
        .insert(insertData)
        .select('id')
        .single();

      if (insertError) {
        console.error('Failed to create generated content:', insertError);
        console.error('Insert error details:', {
          message: insertError.message,
          code: insertError.code,
          details: insertError.details,
          hint: insertError.hint
        });
        
        // ROLLBACK: Restore UI state
        queryClient.invalidateQueries({ queryKey: ['scheduled-reddit-posts', projectId, user.id] });
        
        // Specific error messages for common issues
        if (insertError.message?.includes('duplicate key') || insertError.code === '23505') {
          throw new Error('A duplicate post already exists in Generated Posts');
        } else if (insertError.message?.includes('permission') || insertError.message?.includes('policy')) {
          throw new Error('Permission denied. Please check your account permissions.');
        } else {
          throw new Error(`Failed to create generated post: ${insertError.message}`);
        }
      }

      console.log('Successfully inserted generated content:', insertedPost);

      // Delete from scheduled_reddit_posts with race condition protection
      console.log('Deleting scheduled post:', postId);
      
      const { error: deleteError, count } = await supabase
        .from('scheduled_reddit_posts')
        .delete({ count: 'exact' })
        .eq('id', postId)
        .eq('user_id', user.id);

      if (deleteError) {
        console.error('Failed to delete scheduled post:', deleteError);
        console.error('Delete error details:', {
          message: deleteError.message,
          code: deleteError.code,
          details: deleteError.details,
          hint: deleteError.hint
        });
        
        // CLEANUP: Remove the generated content we just created
        if (insertedPost?.id) {
          console.log('Cleaning up generated content due to delete failure');
          await supabase.from('generated_content').delete().eq('id', insertedPost.id);
        }
        
        // ROLLBACK: Restore UI state
        queryClient.invalidateQueries({ queryKey: ['scheduled-reddit-posts', projectId, user.id] });
        
        if (deleteError.message?.includes('permission') || deleteError.message?.includes('policy')) {
          throw new Error('Permission denied when deleting scheduled post. Please check your account permissions.');
        } else {
          throw new Error(`Failed to delete scheduled post: ${deleteError.message}`);
        }
      }

      // RACE CONDITION CHECK: Verify deletion actually happened
      if (count === 0) {
        console.error('Delete returned count=0, post may have been already deleted');
        
        // Post was already deleted by another process
        if (insertedPost?.id) {
          console.log('Cleaning up generated content due to race condition');
          await supabase.from('generated_content').delete().eq('id', insertedPost.id);
        }
        queryClient.invalidateQueries({ queryKey: ['scheduled-reddit-posts', projectId, user.id] });
        throw new Error('Post was already cancelled by another process');
      }

      console.log('Successfully completed cancellation process');

      toast({
        title: 'Post Moved Back to Generated Posts',
        description: `"${currentPost.post_title}" is now available in Generated Posts and can be rescheduled.`,
      });

      // Refresh both queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['generated-content', projectId, 'reddit'] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-reddit-posts', projectId, user.id] });

    } catch (error) {
      console.error('Cancel operation failed with error:', error);
      
      // Enhanced error message handling
      let errorMessage = 'Failed to cancel scheduled post. Please try again.';
      
      if (error?.message) {
        if (error.message.includes('not found') || error.message.includes('already cancelled')) {
          errorMessage = 'This post has already been cancelled or removed.';
        } else if (error.message.includes('duplicate')) {
          errorMessage = 'This post already exists in Generated Posts.';
        } else if (error.message.includes('permission') || error.message.includes('policy')) {
          errorMessage = 'You do not have permission to cancel this post. Please refresh the page and try again.';
        } else if (error.message.includes('missing required')) {
          errorMessage = 'Post data is incomplete and cannot be moved back to Generated Posts.';
        } else {
          errorMessage = `Cancellation failed: ${error.message}`;
        }
      }
      
      toast({
        title: 'Cancel Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
      // Clean up debounce tracking
      setLastCancelTime(prev => {
        const updated = { ...prev };
        delete updated[postId];
        return updated;
      });
    }
  };

  // Handle deleting a generated post
  const handleDeleteGeneratedPost = async (postId: string) => {
    if (!user?.id) {
      toast({
        title: 'Error',
        description: 'You must be logged in to delete posts.',
        variant: 'destructive',
      });
      return;
    }

    // Confirm deletion
    if (!confirm('Are you sure you want to delete this generated post?')) {
      return;
    }

    setDeletingId(postId);
    
    // Store original data for potential rollback
    const originalData = generatedPosts;
    
    try {
      // OPTIMISTIC UPDATE: Remove from UI immediately
      queryClient.setQueryData(['generated-content', projectId, 'reddit'], (oldData: any) => {
        if (!oldData) return oldData;
        return oldData.filter((post: any) => post.id !== postId);
      });

      // Get post title for success message
      const postToDelete = originalData.find(p => p.id === postId);
      const postTitle = postToDelete?.title || 'Unknown post';

      // Perform the deletion
      const { error: deleteError } = await supabase
        .from('generated_content')
        .delete()
        .eq('id', postId)
        .eq('user_id', user.id);

      if (deleteError) {
        console.error('Delete error:', deleteError);
        // ROLLBACK: Restore original data on error
        queryClient.setQueryData(['generated-content', projectId, 'reddit'], originalData);
        throw deleteError;
      }

      toast({
        title: 'Post Deleted',
        description: `"${postTitle}" has been deleted successfully.`,
      });

    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: 'Delete Failed',
        description: error.message || 'Failed to delete generated post. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  // Handle copy content
  const handleCopyText = useCallback((text: string, contentId: string) => {
    if (!text) {
      toast({
        title: "Cannot copy",
        description: "There is no content to copy.",
        variant: "destructive"
      });
      return;
    }

    navigator.clipboard.writeText(text).then(() => {
      setCopied(prev => ({ ...prev, [contentId]: true }));
      toast({
        title: "Content copied",
        description: "The post content has been copied to your clipboard.",
      });
      
      setTimeout(() => {
        setCopied(prev => ({ ...prev, [contentId]: false }));
      }, 2000);
    }).catch(() => {
      toast({
        title: "Copy failed",
        description: "Could not copy content to clipboard.",
        variant: "destructive"
      });
    });
  }, [toast]);

  // Open editor for a post
  const openEditor = (postId: string) => {
    navigate(`/projects/${projectId}/reddit/${postId}`);
  };

  // Open editor for a scheduled post
  const openScheduledPostEditor = (scheduledPostId: string) => {
    navigate(`/projects/${projectId}/reddit/scheduled/${scheduledPostId}`);
  };

  // Get status color for scheduled posts
  const getScheduledStatusColor = (status: string) => {
    switch (status) {
      case POST_STATUS.SENT: return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case POST_STATUS.APPROVED_FOR_PUBLISHING: return 'bg-blue-50 text-blue-700 border-blue-200';
      case POST_STATUS.PUBLISHING: return 'bg-blue-50 text-blue-700 border-blue-200';
      case POST_STATUS.QUEUED_BY_AGENT: return 'bg-blue-50 text-blue-700 border-blue-200';
      case POST_STATUS.AWAITING_APPROVAL: return 'bg-amber-50 text-amber-700 border-amber-200';
      case POST_STATUS.ERROR_POSTING: return 'bg-red-50 text-red-700 border-red-200';
      case POST_STATUS.CANCELLED: return 'bg-gray-50 text-gray-700 border-gray-200';
      default: return 'bg-zinc-50 text-zinc-700 border-zinc-200';
    }
  };

  // Get status icon for scheduled posts
  const getScheduledStatusIcon = (status: string) => {
    switch (status) {
      case POST_STATUS.SENT: return <CheckCircle className="h-4 w-4" />;
      case POST_STATUS.APPROVED_FOR_PUBLISHING: return <Clock className="h-4 w-4" />;
      case POST_STATUS.PUBLISHING: return <RefreshCw className="h-4 w-4 animate-spin" />;
      case POST_STATUS.QUEUED_BY_AGENT: return <Bot className="h-4 w-4" />;
      case POST_STATUS.AWAITING_APPROVAL: return <AlertCircle className="h-4 w-4" />;
      case POST_STATUS.ERROR_POSTING: return <AlertCircle className="h-4 w-4" />;
      case POST_STATUS.CANCELLED: return <X className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  // Get human-readable status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case POST_STATUS.SENT: return 'Posted';
      case POST_STATUS.APPROVED_FOR_PUBLISHING: return 'Ready to Post';
      case POST_STATUS.PUBLISHING: return 'Publishing';
      case POST_STATUS.QUEUED_BY_AGENT: return 'Queued';
      case POST_STATUS.AWAITING_APPROVAL: return 'Awaiting Approval';
      case POST_STATUS.ERROR_POSTING: return 'Failed';
      case POST_STATUS.CANCELLED: return 'Cancelled';
      default: return status;
    }
  };

  // Handle publishing a post immediately
  const handlePublishNow = async (postId: string) => {
    if (!user?.id) {
      toast({
        title: 'Error',
        description: 'You must be logged in to publish posts.',
        variant: 'destructive',
      });
      return;
    }

    try {
      toast({
        title: 'Publishing Post',
        description: 'Publishing your post via Reddit API...',
      });

      // Primary: Use Reddit API (enhanced publisher)
      const { data, error } = await supabase.functions.invoke('reddit-publisher-enhanced', {
        body: { postId }
      });

      if (error) {
        console.error('API publish error:', error);
        throw error;
      }

      if (data.status === 'error') {
        // API failed - you could optionally try Playwright fallback here
        // For now, just show the error
        throw new Error(data.message || 'API publishing failed');
      }

      toast({
        title: 'Publishing Started',
        description: 'Your post is being published via Reddit API. Check back in a moment for updates.',
      });

      // Refetch data to update UI
      queryClient.invalidateQueries({ queryKey: ['scheduled-reddit-posts', projectId, user.id] });
    } catch (error) {
      console.error('Publish error:', error);
      toast({
        title: 'Publishing Failed',
        description: error.message || 'Failed to publish post. Please check your Reddit credentials.',
        variant: 'destructive',
      });
    }
  };

  // Handle triggering the scheduler manually
  const handleTriggerScheduler = async () => {
    try {
      toast({
        title: 'Checking for Posts',
        description: 'Scanning for scheduled posts that are ready to publish...',
      });

      const { data, error } = await supabase.functions.invoke('reddit-post-scheduler', {
        body: {}
      });

      if (error) {
        console.error('Scheduler error:', error);
        throw error;
      }

      const processedCount = data?.processed_posts || 0;
      
      if (processedCount > 0) {
        toast({
          title: 'Posts Published',
          description: `Started publishing ${processedCount} scheduled posts.`,
        });
      } else {
        toast({
          title: 'No Posts Due',
          description: 'No scheduled posts are ready to be published at this time.',
        });
      }

      // Refetch data to update UI
      queryClient.invalidateQueries({ queryKey: ['scheduled-reddit-posts', projectId, user.id] });
    } catch (error) {
      console.error('Scheduler error:', error);
      toast({
        title: 'Scheduler Failed',
        description: error.message || 'Failed to run scheduler. Please try again.',
        variant: 'destructive',
      });
    }
  };


  // Helper function to get day header text
  const getDayHeaderText = (date: Date) => {
    if (isToday(date)) {
      return `Today | ${format(date, 'MMM d')}`;
    } else if (isTomorrow(date)) {
      return `Tomorrow | ${format(date, 'MMM d')}`;
    } else {
      return `${format(date, 'EEEE')} | ${format(date, 'MMM d')}`;
    }
  };

  // Helper function to group posts by date
  const groupPostsByDate = (posts: ScheduledRedditPost[]) => {
    const grouped: { [key: string]: ScheduledRedditPost[] } = {};
    
    posts.forEach(post => {
      const postDate = startOfDay(parseISO(post.scheduled_at_utc));
      const dateKey = format(postDate, 'yyyy-MM-dd');
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(post);
    });

    // Sort posts within each day by time
    Object.keys(grouped).forEach(dateKey => {
      grouped[dateKey].sort((a, b) => 
        new Date(a.scheduled_at_utc).getTime() - new Date(b.scheduled_at_utc).getTime()
      );
    });

    return grouped;
  };

  // Helper function to check if error is flair-related
  const isFlairError = (errorMessage: string | null | undefined): boolean => {
    if (!errorMessage) return false;
    
    const flairErrorPatterns = [
      'SUBMIT_VALIDATION_FLAIR_REQUIRED',
      'INVALID_FLAIR_ID',
      'FLAIR_NOT_FOUND',
      'FLAIR_TEMPLATE_INVALID',
      'flair',
      'post must contain post flair',
      'your post must contain post flair',
      'flair is required',
      'must have flair',
      'please add flair',
      'submission requires flair',
      'invalid flair selection',
      'flair id not found'
    ];
    
    return flairErrorPatterns.some(pattern => 
      errorMessage.toLowerCase().includes(pattern.toLowerCase())
    );
  };

  // Function to open flair retry dialog
  const openFlairRetryDialog = (postId: string) => {
    setRetryingPostId(postId);
    setSelectedFlairForRetry('');
    setFlairRetryDialogOpen(true);
  };

  // Handle retry with flair
  const handleRetryWithFlair = async () => {
    if (!retryingPostId || !user?.id) return;

    // Store the post ID before clearing state variables
    const postIdToRetry = retryingPostId;

    try {
      // First, update the post with the selected flair and reset status
      await updatePostStatus(postIdToRetry, {
        status: 'approved_for_publishing',
        error_message: null,
        preferred_flair: selectedFlairForRetry || null,
        retry_count: 0 // Reset retry count for manual retry with flair
      });

      toast({
        title: "Publishing Post",
        description: selectedFlairForRetry 
          ? `Publishing post with flair ID "${selectedFlairForRetry}"...`
          : "Publishing post without flair..."
      });

      // Close dialog first to show progress
      setFlairRetryDialogOpen(false);
      setRetryingPostId(null);
      setSelectedFlairForRetry('');

      // Directly call the publisher Edge Function instead of waiting for cron job
      const { data, error } = await supabase.functions.invoke('reddit-publisher-enhanced', {
        body: { postId: postIdToRetry }
      });

      if (error) {
        console.error('Publisher error:', error);
        throw error;
      }

      if (data.status === 'error') {
        throw new Error(data.message || 'Publishing failed');
      }

      // Check if it was successful
      const results = data.data?.results?.[0];
      if (results?.status === 'sent') {
        toast({
          title: "Post Published Successfully! 🎉",
          description: `Your post has been published to r/${results.message?.includes('r/') ? results.message.split('r/')[1] : 'reddit'}`,
        });
      } else if (results?.status === 'error_posting') {
        toast({
          title: "Publishing Failed",
          description: results.message || 'Unknown error occurred',
          variant: "destructive"
        });
      } else {
        toast({
          title: "Publishing Initiated",
          description: "Your post is being processed. Check back in a moment for updates.",
        });
      }

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['scheduled-reddit-posts', projectId, user.id] });
      
    } catch (error) {
      console.error('Error retrying post with flair:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to retry post. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Generate timeline days - include both future days and days with existing posts
  const generateTimelineDays = (postsData: ScheduledRedditPost[]) => {
    const days = new Set<string>();
    const today = startOfDay(new Date());
    
    // Add next 14 days starting from today
    for (let i = 0; i < 14; i++) {
      const day = addDays(today, i);
      days.add(format(day, 'yyyy-MM-dd'));
    }
    
    // Add any days that have scheduled posts (even if in the past or far future)
    postsData.forEach(post => {
      const postDate = startOfDay(parseISO(post.scheduled_at_utc));
      days.add(format(postDate, 'yyyy-MM-dd'));
    });
    
    // Convert back to Date objects and sort
    const sortedDays = Array.from(days)
      .map(dateString => parseISO(dateString))
      .sort((a, b) => a.getTime() - b.getTime());
    
    return sortedDays;
  };

  if (isLoadingGenerated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-6 max-w-7xl">
      <div className="flex flex-col space-y-8">
        {/* Header */}
        <motion.div 
          className="flex items-center justify-between"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-500 to-blue-500 bg-clip-text text-transparent">
                Reddit Content
              </h1>
              <p className="text-zinc-500 mt-1">
                Manage and schedule your Reddit posts
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => navigate(`/projects/${projectId}/launch/reddit`)}
            >
              <Plus className="h-4 w-4" />
              Generate New Posts
            </Button>
            
            {scheduledPosts.filter(p => 
              p.status === 'approved_for_publishing' && new Date(p.scheduled_at_utc) <= new Date()
            ).length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 border-green-300 text-green-700 hover:bg-green-50"
                onClick={handleTriggerScheduler}
              >
                <Send className="h-4 w-4" />
                Publish Due Posts ({scheduledPosts.filter(p => 
                  p.status === 'approved_for_publishing' && new Date(p.scheduled_at_utc) <= new Date()
                ).length})
              </Button>
            )}
          </div>
        </motion.div>

        {/* Live Activity Status */}
        {scheduledPosts.filter(p => 
          p.status === POST_STATUS.PUBLISHING || 
          (p.status === POST_STATUS.APPROVED_FOR_PUBLISHING && new Date(p.scheduled_at_utc) <= new Date())
        ).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
          >
            <Card className="border-2 border-blue-200 dark:border-blue-800/40 bg-gradient-to-r from-blue-50 via-blue-50 to-blue-50 dark:from-blue-950/30 dark:via-blue-950/30 dark:to-blue-950/30 shadow-lg overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-sky-500">
                        <RefreshCw className="h-6 w-6 text-white animate-spin" />
                      </div>
                      <div className="absolute -top-1 -right-1 h-4 w-4 bg-green-500 rounded-full animate-pulse"></div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                        🚀 Live Publishing Activity
                        <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-800/40 animate-pulse">
                          ACTIVE
                        </Badge>
                      </h3>
                      <p className="text-sm text-zinc-600 dark:text-zinc-400">Your Reddit posts are being published automatically</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {scheduledPosts.filter(p => 
                        p.status === 'publishing' || 
                        (p.status === 'approved_for_publishing' && new Date(p.scheduled_at_utc) <= new Date())
                      ).length}
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 font-medium">Active Now</div>
                  </div>
                </div>

                {/* Active Posts List */}
                <div className="space-y-3">
                  {scheduledPosts
                    .filter(p => 
                      p.status === 'publishing' || 
                      (p.status === 'approved_for_publishing' && new Date(p.scheduled_at_utc) <= new Date())
                    )
                    .slice(0, 3)
                    .map((post, index) => (
                    <motion.div
                      key={post.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white/70 dark:bg-zinc-800/70 backdrop-blur-sm border border-white/50 dark:border-zinc-700/50 rounded-lg p-4 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <div className={cn(
                            "flex items-center justify-center w-8 h-8 rounded-full",
                            post.status === 'publishing' 
                              ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" 
                              : "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                          )}>
                            {post.status === 'publishing' ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Clock className="h-4 w-4" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-zinc-900 dark:text-zinc-100 text-sm">r/{post.subreddit_name}</span>
                              <Badge className={cn(
                                "text-xs px-2 py-0.5",
                                post.status === 'publishing' 
                                  ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/40 animate-pulse" 
                                  : "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/40"
                              )}>
                                {post.status === 'publishing' ? 'Publishing...' : '⏳ Queued'}
                              </Badge>
                            </div>
                            <p className="text-xs text-zinc-600 dark:text-zinc-400 truncate">
                              {post.post_title}
                            </p>
                          </div>
                        </div>
                        <div className="text-right text-xs text-zinc-500 dark:text-zinc-400">
                          {post.status === 'publishing' ? (
                            <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 font-medium">
                              <div className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full animate-pulse"></div>
                              Live
                            </div>
                          ) : (
                            <div>
                              {new Date(post.scheduled_at_utc) <= new Date() ? 'Due now' : 
                               formatDistance(new Date(post.scheduled_at_utc), new Date(), { addSuffix: true })}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {scheduledPosts.filter(p => 
                  p.status === 'publishing' || 
                  (p.status === 'approved_for_publishing' && new Date(p.scheduled_at_utc) <= new Date())
                ).length > 3 && (
                  <div className="mt-3 pt-3 border-t border-white/50 dark:border-zinc-700/50">
                    <p className="text-xs text-zinc-600 dark:text-zinc-400 text-center">
                      +{scheduledPosts.filter(p => 
                        p.status === 'publishing' || 
                        (p.status === 'approved_for_publishing' && new Date(p.scheduled_at_utc) <= new Date())
                      ).length - 3} more posts in queue
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Generation progress */}
        {isGenerating && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="mb-6 border-0 shadow-lg bg-white dark:bg-zinc-800 border-blue-200 dark:border-blue-800/30 overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold flex items-center gap-2 text-zinc-900 dark:text-zinc-100">
                    <Sparkles className="h-5 w-5 text-blue-500 animate-pulse" />
                    Generating Reddit Post
                  </h3>
                  
                  {generationTimestamp && (Date.now() - generationTimestamp > 15000) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-sm"
                      onClick={() => {
                        setIsGenerating(false);
                        setIsWaitingForContent(false);
                        setGenerationId(null);
                        
                        const urlParams = new URLSearchParams(window.location.search);
                        urlParams.delete("reddit_generating");
                        window.history.replaceState(
                          {},
                          "",
                          window.location.pathname + (urlParams.toString() ? "?" + urlParams.toString() : "")
                        );
                        toast({
                          title: "Generation canceled",
                          description: "You can try again if needed.",
                          variant: "default",
                        });
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>

                <Progress value={Math.max(10, loadingProgress)} className="h-2 mb-2" />
                
                <div className="text-sm text-muted-foreground dark:text-zinc-400">
                  {loadingProgress < 30 ? "Analyzing subreddit community patterns..." : 
                   loadingProgress < 60 ? "Crafting authentic community content..." : 
                   loadingProgress < 90 ? "Finalizing post format and style..." :
                   isWaitingForContent ? "Waiting for content to appear..." :
                   "Your Reddit post is almost ready..."}
                   
                  {isWaitingForContent && generationTimestamp && (Date.now() - generationTimestamp > 12000) && (
                    <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                      Generation is taking longer than usual. Your content will appear shortly.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Recently Posted Success Banner */}
        {scheduledPosts.filter(p => p.status === POST_STATUS.SENT && 
          new Date().getTime() - new Date(p.updated_at || p.created_at).getTime() < RECENT_POST_WINDOW).length > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="border-2 border-green-200 dark:border-green-800/40 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 shadow-lg">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-green-900 dark:text-green-100 flex items-center gap-2">
                      ✅ Successfully Posted!
                      <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-800/40">
                        {scheduledPosts.filter(p => p.status === 'sent' && 
                          new Date().getTime() - new Date(p.updated_at || p.created_at).getTime() < RECENT_POST_WINDOW).length} recent
                      </Badge>
                    </h4>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      {scheduledPosts.filter(p => p.status === 'sent' && 
                        new Date().getTime() - new Date(p.updated_at || p.created_at).getTime() < RECENT_POST_WINDOW)
                          .slice(0, 2)
                          .map(p => `r/${p.subreddit_name}`)
                          .join(', ')} - Check Reddit for engagement!
                    </p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-green-300 dark:border-green-800/40 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-950/30"
                    onClick={() => setSelectedTab('scheduled')}
                  >
                    View Posts
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Stats Cards */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card className="relative overflow-hidden border-0 shadow-lg bg-white dark:bg-zinc-800">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-950/30 dark:to-blue-950/30"></div>
            <CardContent className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">Generated Posts</p>
                  <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{generatedPosts.length}</p>
                </div>
                <Bot className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border-0 shadow-lg bg-white dark:bg-zinc-800">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30"></div>
            <CardContent className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">Scheduled</p>
                  <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                    {filteredScheduledPosts.length}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border-0 shadow-lg bg-white dark:bg-zinc-800">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30"></div>
            <CardContent className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">Published</p>
                  <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                    {filteredPublishedPosts.length}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden border-0 shadow-lg bg-white dark:bg-zinc-800">
            <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-950/30 dark:to-pink-950/30"></div>
            <CardContent className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">Failed</p>
                  <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                    {scheduledPosts.filter(p => p.status === POST_STATUS.ERROR_POSTING).length}
                  </p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          className="flex items-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          {selectedTab === 'generated' && (
            <Select value={selectedFilter} onValueChange={setSelectedFilter}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Posts</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>
          )}
          {selectedTab === 'scheduled' && (
            <Select value={scheduledStatusFilter} onValueChange={setScheduledStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Scheduled</SelectItem>
                <SelectItem value="active">Active/Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="awaiting_approval">Awaiting Approval</SelectItem>
              </SelectContent>
            </Select>
          )}
        </motion.div>

        {/* Subreddit filters for generated posts */}
        {selectedTab === 'generated' && Object.keys(subredditCounts).length > 0 && (
          <motion.div
            className="flex flex-wrap gap-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
          >
            {activeSubreddit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveSubreddit(null)}
                className="flex items-center space-x-1 text-xs h-7 px-2 border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800"
              >
                Show All
              </Button>
            )}
            {Object.entries(subredditCounts).map(([subreddit, count]) => (
              <Button
                key={subreddit}
                variant="outline"
                size="sm"
                onClick={() => setActiveSubreddit(activeSubreddit === subreddit ? null : subreddit)}
                className={cn(
                  "flex items-center space-x-1 text-xs h-7 px-2",
                  activeSubreddit === subreddit 
                    ? "border-blue-400 dark:border-blue-600 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 font-medium"
                    : "border-blue-200 dark:border-blue-800/40 bg-blue-50 dark:bg-blue-950/30 text-blue-800 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                )}
              >
                r/{subreddit} ({count})
              </Button>
            ))}
          </motion.div>
                 )}

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-6">
            <TabsList className="grid w-fit grid-cols-3 bg-zinc-100 dark:bg-zinc-800 p-1 rounded-xl">
              <TabsTrigger 
                value="generated" 
                className="data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:shadow-sm dark:data-[state=active]:text-zinc-100 text-zinc-600 dark:text-zinc-300 rounded-lg"
              >
                <Bot className="h-4 w-4 mr-2" />
                Generated Posts ({generatedPosts.length})
              </TabsTrigger>
              <TabsTrigger 
                value="scheduled" 
                className="data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:shadow-sm dark:data-[state=active]:text-zinc-100 text-zinc-600 dark:text-zinc-300 rounded-lg"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Scheduled ({filteredScheduledPosts.length})
              </TabsTrigger>
              <TabsTrigger 
                value="published" 
                className="data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:shadow-sm dark:data-[state=active]:text-zinc-100 text-zinc-600 dark:text-zinc-300 rounded-lg"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Published ({filteredPublishedPosts.length})
              </TabsTrigger>
            </TabsList>

            {/* Generated Posts Tab */}
            <TabsContent value="generated" className="space-y-4">
              {filteredGeneratedPosts.length === 0 ? (
                <Card className="p-12 text-center border-0 shadow-lg bg-white dark:bg-zinc-800">
                  <Bot className="w-16 h-16 text-gray-400 dark:text-zinc-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 dark:text-zinc-300 mb-2">No Reddit Posts Yet</h3>
                  <p className="text-gray-500 dark:text-zinc-400 mb-6">Generate your first Reddit posts to get started!</p>
                  <Button
                    onClick={() => navigate(`/projects/${projectId}/launch/reddit`)}
                    className="gap-2 bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600"
                  >
                    <Plus className="h-4 w-4" />
                    Generate Reddit Posts
                  </Button>
                </Card>
              ) : (
                filteredGeneratedPosts.map((content, index) => {
                  const subreddit = content.metadata?.subreddit || "unknown";
                  const postType = content.metadata?.topic || "Post";
                  const hiddenGoal = content.metadata?.hiddenGoal || "None";
                  const truncatedContent = content.content && content.content.length > MAX_CONTENT_PREVIEW
                    ? content.content.substring(0, MAX_CONTENT_PREVIEW) + "..."
                    : content.content;
                  const createdAt = content.created_at 
                    ? formatDistance(new Date(content.created_at), new Date(), { addSuffix: true })
                    : "recently";
                    

                  return (
                    <motion.div
                      key={content.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <Card 
                        className="group mb-4 border-0 shadow-sm hover:shadow-xl bg-gradient-to-br from-white to-zinc-50/50 dark:from-zinc-800 dark:to-zinc-800/80 overflow-hidden transition-all duration-300 cursor-pointer hover:border-orange-200 dark:hover:border-orange-800/40 hover:-translate-y-1 ring-1 ring-zinc-200/50 dark:ring-zinc-700/50 hover:ring-orange-200/50 dark:hover:ring-orange-800/50"
                        onClick={() => openEditor(content.id)}
                      >
                        <CardContent className="p-0">
                          {/* Header with subreddit and actions */}
                          <div className="bg-gradient-to-r from-orange-50 via-white to-orange-50/30 dark:from-zinc-900/50 dark:via-zinc-800 dark:to-zinc-900/30 border-b border-orange-100/50 dark:border-zinc-700/50 p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {/* Reddit-style subreddit indicator */}
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center shadow-sm">
                                    <span className="text-white text-sm font-bold">r/</span>
                                  </div>
                                  <div>
                                    <button 
                                      className="font-semibold text-zinc-900 dark:text-zinc-100 hover:text-orange-600 dark:hover:text-orange-400 transition-colors text-sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveSubreddit(subreddit);
                                      }}
                                    >
                                      r/{subreddit}
                                    </button>
                                    <div className="flex items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400">
                                      <UserRound className="h-3 w-3" />
                                      <span>Created by you • {createdAt}</span>
                                    </div>
                                  </div>
                                </div>
                            </div>
                            
                              {/* Schedule Button */}
                              <Button
                                size="sm"
                                className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-200 font-medium px-4"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPost(content);
                                  setScheduleForm({
                                    subreddit: '',
                                    scheduledAt: '',
                                    customTitle: '',
                                    customContent: ''
                                  });
                                  setIsScheduleModalOpen(true);
                                }}
                              >
                                <Calendar className="h-4 w-4" />
                                Schedule
                              </Button>
                            </div>
                              </div>
                              
                          {/* Main content */}
                          <div className="p-6">
                              {/* Title */}
                            <h3 className="text-xl font-bold mb-3 text-zinc-900 dark:text-zinc-100 leading-tight group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors line-clamp-2">
                                {content.title}
                              </h3>
                              
                              {/* Content preview */}
                            <div className="text-zinc-700 dark:text-zinc-300 mb-4 leading-relaxed line-clamp-3">
                                {truncatedContent}
                              </div>
                            
                            {/* Tags */}
                            <div className="flex flex-wrap gap-2 mb-4">
                              <Badge className="bg-gradient-to-r from-blue-100 to-blue-100 dark:from-blue-900/30 dark:to-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/40 font-medium px-3 py-1">
                                {postType}
                              </Badge>
                              {hiddenGoal !== "None" && hiddenGoal !== "There's no hidden goal" && (
                                <Badge className="bg-gradient-to-r from-blue-100 to-pink-100 dark:from-blue-900/30 dark:to-pink-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/40 font-medium px-3 py-1">
                                  Goal: {hiddenGoal}
                                </Badge>
                              )}
                            </div>
                              
                              {/* Action bar */}
                            <div className="flex items-center justify-between pt-4 border-t border-zinc-100 dark:border-zinc-700/50">
                              <div className="flex items-center gap-6">
                                <div className="flex items-center gap-2 text-zinc-500 dark:text-zinc-400 text-sm hover:text-orange-600 dark:hover:text-orange-400 transition-colors cursor-pointer">
                                  <MessageSquare className="h-4 w-4" />
                                  <span>Discuss</span>
                                </div>
                                
                                <div className="flex items-center gap-2 text-zinc-500 dark:text-zinc-400 text-sm hover:text-orange-600 dark:hover:text-orange-400 transition-colors cursor-pointer">
                                  <Share className="h-4 w-4" />
                                  <span>Share</span>
                                </div>
                                </div>
                                
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 gap-2 font-medium"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopyText(content.content || "", content.id);
                                  }}
                                >
                                  {copied[content.id] ? (
                                    <>
                                      <ClipboardCheck className="h-4 w-4 text-green-600" />
                                      Copied
                                    </>
                                  ) : (
                                    <>
                                      <Clipboard className="h-4 w-4" />
                                      Copy
                                    </>
                                  )}
                                </Button>

                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-red-400 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/30 gap-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteGeneratedPost(content.id);
                                  }}
                                  disabled={deletingId === content.id}
                                >
                                  {deletingId === content.id ? (
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <>
                                    <Trash2 className="h-4 w-4" />
                                      Delete
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })
              )}
            </TabsContent>

            {/* Scheduled Posts Tab */}
            <TabsContent value="scheduled" className="space-y-4">
              {filteredScheduledPosts.length === 0 ? (
                <Card className="p-12 text-center border-0 shadow-lg bg-white dark:bg-zinc-800">
                  <Calendar className="w-16 h-16 text-gray-400 dark:text-zinc-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 dark:text-zinc-300 mb-2">No Scheduled Posts</h3>
                  <p className="text-gray-500 dark:text-zinc-400">Schedule some posts to see them here.</p>
                </Card>
              ) : (
                <div className="space-y-6">
                  {(() => {
                    const groupedPosts = groupPostsByDate(filteredScheduledPosts);
                    const timelineDays = generateTimelineDays(filteredScheduledPosts);
                    
                    return timelineDays.map((day) => {
                      const dateKey = format(day, 'yyyy-MM-dd');
                      const postsForDay = groupedPosts[dateKey] || [];
                      const dayHeaderText = getDayHeaderText(day);
                      
                      // Show days that have posts, or are within the next 3 days from today
                      const shouldShowDay = postsForDay.length > 0 || day <= addDays(new Date(), 3);
                      
                      if (!shouldShowDay) return null;
                      
                      return (
                        <motion.div
                          key={dateKey}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className="space-y-4"
                        >
                          {/* Day Header */}
                          <div className="flex items-center gap-4">
                            <h2 className="text-lg font-semibold text-zinc-700 dark:text-zinc-300 tracking-wide">
                              {dayHeaderText}
                            </h2>
                            <div className="flex-1 h-px bg-gradient-to-r from-zinc-200 to-transparent dark:from-zinc-700"></div>
                          </div>
                          
                          {/* Posts for this day */}
                          <div className="space-y-3">
                            {postsForDay.length === 0 ? (
                              <div className="ml-8 py-4">
                                <div className="flex items-center gap-3 text-zinc-400 dark:text-zinc-500">
                                  <div className="w-16 text-sm font-mono">
                                    {isToday(day) ? '10:00 am' : '12:00 pm'}
                                  </div>
                                  <div className="flex-1 italic text-sm">
                                    No posts scheduled for this day
                                  </div>
                                </div>
                              </div>
                            ) : (
                              postsForDay.map((post, index) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                                  className="flex items-start gap-3"
                                >
                                  {/* Time */}
                                  <div className="w-16 pt-3">
                                    <div className="flex items-center gap-2">
                                      <Clock className="h-3 w-3 text-blue-500" />
                                      <span className="text-sm font-mono text-zinc-600 dark:text-zinc-400">
                                        {format(parseISO(post.scheduled_at_utc), 'h:mm a').toLowerCase()}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {/* Post Card */}
                                  <Card className="flex-1 overflow-hidden hover:shadow-md transition-all duration-200 border-0 shadow-lg bg-white dark:bg-zinc-800 hover:border-blue-300 dark:hover:border-blue-600">
                                    <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-2 mb-2">
                                          {/* Reddit icon placeholder */}
                                          <div className="w-4 h-4 bg-orange-500 rounded-sm flex items-center justify-center">
                                            <span className="text-white text-xs font-bold">r</span>
                                          </div>
                              <Badge className={cn(
                                "text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1",
                                getScheduledStatusColor(post.status)
                              )}>
                                {getScheduledStatusIcon(post.status)}
                                {getStatusLabel(post.status)}
                              </Badge>
                              <span className="text-sm text-zinc-500 dark:text-zinc-400">r/{post.subreddit_name}</span>
                              {post.preferred_flair && (
                                <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/40">
                                  🏷️ {post.preferred_flair}
                                </Badge>
                              )}
                              {post.status === 'publishing' && (
                                <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/40 animate-pulse">
                                  🔄 LIVE
                                </Badge>
                              )}
                            </div>
                            
                                        <div className="flex items-center gap-2">
                                          {post.reddit_post_id && (
                                            <Button 
                                              size="sm" 
                                              variant="ghost"
                                              className="h-8 w-8 p-0 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                                              onClick={() => window.open(`https://reddit.com/r/${post.subreddit_name}/comments/${post.reddit_post_id}`, '_blank')}
                                              title="View on Reddit"
                                            >
                                              <ExternalLink className="h-4 w-4" />
                                            </Button>
                                          )}
                                          {post.status === 'approved_for_publishing' && new Date(post.scheduled_at_utc) <= new Date() && (
                                            <Button 
                                              size="sm" 
                                              variant="ghost"
                                              className="h-8 w-8 p-0 text-green-500 hover:text-green-600"
                                              onClick={() => handlePublishNow(post.id)}
                                              title="Publish Now"
                                            >
                                              <Send className="h-4 w-4" />
                                            </Button>
                                          )}
                                          <Button 
                                            size="sm" 
                                            variant="ghost"
                                            className="h-8 w-8 p-0 text-blue-500 hover:text-blue-600"
                                            onClick={() => openScheduledPostEditor(post.id)}
                                            title="Edit this scheduled post"
                                          >
                                            <Edit className="h-4 w-4" />
                                          </Button>
                                          <Button 
                                            size="sm" 
                                            variant="ghost"
                                            className="h-8 w-8 p-0 text-amber-500 hover:text-amber-600"
                                            onClick={() => handleCancelScheduledPost(post.id)}
                                            disabled={deletingId === post.id}
                                            title="Cancel scheduling - move back to Generated Posts"
                                          >
                                            {deletingId === post.id ? (
                                              <RefreshCw className="h-4 w-4 animate-spin" />
                                            ) : (
                                              <X className="h-4 w-4" />
                                            )}
                                          </Button>
                                        </div>
                            </div>
                                      
                                      <h3 className="text-base font-medium text-zinc-900 dark:text-zinc-100 mb-2 line-clamp-2">
                                        {post.post_title}
                                      </h3>

                            {post.status === 'error_posting' && post.error_message && (
                              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/40 rounded-lg p-4 mb-3">
                                <div className="flex items-center gap-2 text-red-700 dark:text-red-300 mb-2">
                                  <AlertCircle className="h-4 w-4" />
                                  <span className="text-sm font-medium">Posting Failed:</span>
                                </div>
                                <p className="text-sm text-red-600 dark:text-red-400 mb-3">{post.error_message}</p>
                                
                                {isFlairError(post.error_message) && (
                                  <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/40 rounded-lg p-3 mb-3">
                                    <div className="flex items-start gap-2">
                                      <div className="flex-shrink-0 w-5 h-5 bg-blue-200 dark:bg-blue-800 rounded-full flex items-center justify-center mt-0.5">
                                        <span className="text-blue-800 dark:text-blue-200 text-xs">ℹ️</span>
                                      </div>
                                      <div className="flex-1">
                                        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                                          Flair Required by Subreddit
                                        </h4>
                                        <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                                          This subreddit requires posts to have a flair tag for organization. 
                                          <strong>Good news:</strong> Once you add a flair, we'll save it and automatically use it for future posts to this subreddit!
                                        </p>
                                        <div className="mt-2 text-xs text-blue-600 dark:text-blue-400 space-y-1">
                                          <div className="flex items-center gap-1">
                                            <span className="font-medium">Option 1:</span>
                                            <span>Choose a different subreddit that doesn't require flairs</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <span className="font-medium">Option 2:</span>
                                            <span>Set up flair once (we'll remember it for future posts)</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                <div className="flex gap-2">
                                  {isFlairError(post.error_message) ? (
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      className="text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-800/40 hover:bg-blue-50 dark:hover:bg-blue-950/50 gap-2"
                                      onClick={() => openFlairRetryDialog(post.id)}
                                    >
                                      <RefreshCw className="h-3 w-3" />
                                      Follow Flair Instructions
                                    </Button>
                                  ) : (
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      className="text-red-600 dark:text-red-400 border-red-300 dark:border-red-800/40 hover:bg-red-50 dark:hover:bg-red-950/50"
                                      onClick={async () => {
                                        try {
                                          await updatePostStatus(post.id, {
                                            status: 'approved_for_publishing',
                                            error_message: null,
                                            retry_count: (post.retry_count || 0) + 1
                                          });
                                          toast({
                                            title: "Post Queued for Retry",
                                            description: "The post has been queued to retry publishing."
                                          });
                                          queryClient.invalidateQueries({ queryKey: ['scheduled-reddit-posts', projectId, user.id] });
                                        } catch (error) {
                                          toast({
                                            title: "Error",
                                            description: "Failed to retry post. Please try again.",
                                            variant: "destructive"
                                          });
                                        }
                                      }}
                                    >
                                      <RefreshCw className="h-3 w-3 mr-1" />
                                      Retry
                                    </Button>
                                  )}
                                </div>
                              </div>
                            )}

                            <p className="text-sm text-zinc-600 dark:text-zinc-300 line-clamp-2">
                              {post.post_content}
                            </p>
                                    </CardContent>
                                  </Card>
                                </motion.div>
                              ))
                            )}
                          </div>
                        </motion.div>
                      );
                    }).filter(Boolean);
                  })()}
                </div>
              )}
            </TabsContent>

            {/* Published Posts Tab */}
            <TabsContent value="published" className="space-y-4">
              {filteredPublishedPosts.length === 0 ? (
                <Card className="p-12 text-center border-0 shadow-lg bg-white dark:bg-zinc-800">
                  <CheckCircle className="w-16 h-16 text-gray-400 dark:text-zinc-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 dark:text-zinc-300 mb-2">No Published Posts</h3>
                  <p className="text-gray-500 dark:text-zinc-400">Your published posts will appear here.</p>
                </Card>
              ) : (
                filteredPublishedPosts.map((post, index) => {
                  const truncatedContent = post.post_content && post.post_content.length > MAX_CONTENT_PREVIEW
                    ? post.post_content.substring(0, MAX_CONTENT_PREVIEW) + "..."
                    : post.post_content;
                  const publishedAt = post.updated_at 
                    ? formatDistance(new Date(post.updated_at), new Date(), { addSuffix: true })
                    : "recently";
                    

                  return (
                    <motion.div
                      key={post.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <Card className="group mb-4 border-0 shadow-sm hover:shadow-xl bg-gradient-to-br from-white to-green-50/30 dark:from-zinc-800 dark:to-zinc-800/80 overflow-hidden transition-all duration-300 hover:border-green-200 dark:hover:border-green-800/40 hover:-translate-y-1 ring-1 ring-zinc-200/50 dark:ring-zinc-700/50 hover:ring-green-200/50 dark:hover:ring-green-800/50">
                        <CardContent className="p-0">
                          {/* Header with subreddit and published status */}
                          <div className="bg-gradient-to-r from-green-50 via-white to-emerald-50/30 dark:from-zinc-900/50 dark:via-zinc-800 dark:to-zinc-900/30 border-b border-green-100/50 dark:border-zinc-700/50 p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {/* Reddit-style subreddit indicator */}
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-sm">
                                    <CheckCircle className="h-4 w-4 text-white" />
                                  </div>
                                  <div>
                                    <div className="font-semibold text-zinc-900 dark:text-zinc-100 text-sm">
                                      r/{post.subreddit_name}
                                    </div>
                                    <div className="flex items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400">
                                      <UserRound className="h-3 w-3" />
                                      <span>Published by you • {publishedAt}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Published Badge */}
                              <Badge className="bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800/40 font-medium px-3 py-1.5">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Published
                              </Badge>
                            </div>
                          </div>
                          
                          {/* Main content */}
                          <div className="p-6">
                            {/* Title */}
                            <h3 className="text-xl font-bold mb-3 text-zinc-900 dark:text-zinc-100 leading-tight line-clamp-2">
                              {post.post_title}
                            </h3>
                            
                            {/* Content preview */}
                            <div className="text-zinc-700 dark:text-zinc-300 mb-4 leading-relaxed line-clamp-3">
                              {truncatedContent}
                            </div>
                            
                            {/* Status Tags */}
                            <div className="flex flex-wrap gap-2 mb-4">
                              <Badge className="bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800/40 font-medium px-3 py-1">
                                {getStatusLabel(post.status)}
                              </Badge>
                              <Badge className="bg-gradient-to-r from-blue-100 to-blue-100 dark:from-blue-900/30 dark:to-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/40 font-medium px-3 py-1">
                                Posted {new Date(post.scheduled_at_utc).toLocaleDateString()}
                              </Badge>
                            </div>
                            
                            {/* Action bar */}
                            <div className="flex items-center justify-between pt-4 border-t border-zinc-100 dark:border-zinc-700/50">
                              <div className="flex items-center gap-6">
                                <div className="flex items-center gap-2 text-zinc-500 dark:text-zinc-400 text-sm hover:text-green-600 dark:hover:text-green-400 transition-colors cursor-pointer">
                                  <MessageSquare className="h-4 w-4" />
                                  <span>Comments</span>
                                </div>
                                
                                <div className="flex items-center gap-2 text-zinc-500 dark:text-zinc-400 text-sm hover:text-green-600 dark:hover:text-green-400 transition-colors cursor-pointer">
                                  <Share className="h-4 w-4" />
                                  <span>Share</span>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                            {post.reddit_post_id && (
                              <Button 
                                    variant="ghost"
                                size="sm" 
                                    className="text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/30 gap-2 font-medium"
                                onClick={() => window.open(`https://reddit.com/r/${post.subreddit_name}/comments/${post.reddit_post_id}`, '_blank')}
                              >
                                    <ExternalLink className="h-4 w-4" /> 
                                View on Reddit
                              </Button>
                            )}
                                
                              <Button 
                                  variant="ghost"
                                size="sm" 
                                  className="text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 gap-2 font-medium"
                                  onClick={() => handleCopyText(post.post_content || "", post.id)}
                            >
                                  {copied[post.id] ? (
                                    <>
                                      <ClipboardCheck className="h-4 w-4 text-green-600" />
                                      Copied
                                    </>
                              ) : (
                                <>
                                      <Clipboard className="h-4 w-4" />
                                      Copy
                                </>
                              )}
                            </Button>
                              </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                  );
                })
              )}
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      {/* Flair Retry Dialog */}
      <Dialog open={flairRetryDialogOpen} onOpenChange={setFlairRetryDialogOpen}>
        <DialogContent className="sm:max-w-2xl bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-sky-500">
                <RefreshCw className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="text-lg">Add Flair to Your Post</div>
                <div className="text-sm font-normal text-zinc-500 dark:text-zinc-400">Required by this subreddit</div>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800/40 rounded-lg p-4 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-shrink-0 w-6 h-6 bg-green-200 dark:bg-green-800 rounded-full flex items-center justify-center">
                  <span className="text-green-800 dark:text-green-200 text-sm">✨</span>
                </div>
                <h4 className="text-sm font-medium text-green-900 dark:text-green-100">
                  One-time setup - We'll remember this for future posts!
                </h4>
              </div>
              <p className="text-xs text-green-700 dark:text-green-300 leading-relaxed">
                After you complete this process once, all future posts to r/{retryingPostId ? scheduledPosts.find(p => p.id === retryingPostId)?.subreddit_name || 'this subreddit' : 'this subreddit'} will automatically use the correct flair. You'll never have to do this again!
              </p>
            </div>

            <div className="bg-zinc-50 dark:bg-zinc-950/30 border border-zinc-200 dark:border-zinc-800/40 rounded-lg p-4">
              <h4 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-3 flex items-center gap-2">
                🔧 How to find the Reddit Flair ID:
              </h4>
              <ol className="text-sm text-zinc-800 dark:text-zinc-200 space-y-2">
                                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-zinc-200 dark:bg-zinc-700 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <span>Go to the subreddit on Reddit and click "Create Post"</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-zinc-200 dark:bg-zinc-700 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <span>Look for the flair options (usually shown as colored tags)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-zinc-200 dark:bg-zinc-700 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <span>Right-click anywhere on the page and select "Inspect" or press <kbd className="px-1 py-0.5 bg-zinc-200 dark:bg-zinc-700 rounded text-xs">F12</kbd></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-zinc-200 dark:bg-zinc-700 rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  <span>Press <kbd className="px-1 py-0.5 bg-zinc-200 dark:bg-zinc-700 rounded text-xs">Ctrl+F</kbd> (or <kbd className="px-1 py-0.5 bg-zinc-200 dark:bg-zinc-700 rounded text-xs">Cmd+F</kbd> on Mac) to open search</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-zinc-200 dark:bg-zinc-700 rounded-full flex items-center justify-center text-xs font-bold">5</span>
                  <span>Search for <code className="bg-zinc-200 dark:bg-zinc-700 px-1 rounded">FlairId</code> in the HTML</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-zinc-200 dark:bg-zinc-700 rounded-full flex items-center justify-center text-xs font-bold">6</span>
                  <span>Copy the long ID that looks like: <code className="bg-zinc-200 dark:bg-zinc-700 px-1 rounded text-xs">f20ee1a6-3ba9-11ef-ab76-fa8f05563c8c</code></span>
                </li>
              </ol>
            </div>


            
            <div className="space-y-3">
              <Label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                Paste the Flair ID here
              </Label>
                              <Input
                  type="text"
                  placeholder="e.g. f20ee1a6-3ba9-11ef-ab76-fa8f05563c8c"
                  value={selectedFlairForRetry}
                  onChange={(e) => setSelectedFlairForRetry(e.target.value)}
                  className="w-full font-mono text-sm"
                />
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                Paste the exact Flair ID you found in Reddit's developer tools
              </p>
            </div>
          </div>
          
          <div className="flex justify-between pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setFlairRetryDialogOpen(false);
                setRetryingPostId(null);
                setSelectedFlairForRetry('');
              }}
              className="text-zinc-600 dark:text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRetryWithFlair}
              disabled={!selectedFlairForRetry.trim()}
              className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed px-6"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Publish with Flair
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Post Dialog */}
      <Dialog open={isScheduleModalOpen} onOpenChange={setIsScheduleModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] p-0 overflow-hidden bg-white dark:bg-zinc-900 rounded-xl shadow-2xl flex flex-col border-zinc-200 dark:border-zinc-700">
          {/* Header - Compact */}
          <div className="bg-gradient-to-br from-blue-50 via-blue-50 to-blue-50 dark:from-blue-950/30 dark:via-blue-950/30 dark:to-blue-950/30 p-4 border-b border-zinc-200 dark:border-zinc-700">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-sky-500">
                  <Calendar className="h-4 w-4 text-white" />
                </div>
                Schedule Reddit Post
              </DialogTitle>
            </DialogHeader>
          </div>
          
          {/* Form */}
          <div className="p-6 space-y-6 overflow-y-auto flex-1">
            <div className="space-y-3">
              <Label htmlFor="subreddit" className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 flex items-center gap-2">
                <Target className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Target Subreddit
              </Label>
              <Select 
                value={scheduleForm.subreddit} 
                onValueChange={(value) => setScheduleForm(prev => ({ ...prev, subreddit: value }))}
                disabled={isLoadingApproved}
              >
                <SelectTrigger className="h-12 bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 focus-visible:ring-blue-500 focus-visible:ring-2 focus-visible:border-blue-500 transition-all text-zinc-900 dark:text-zinc-100">
                  <SelectValue placeholder={
                    isLoadingApproved 
                      ? "Loading subreddits..." 
                      : approvedSubredditsError 
                        ? "Error loading subreddits" 
                        : "Choose a subreddit community"
                  } />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 shadow-lg rounded-lg">
                  {isLoadingApproved ? (
                    <SelectItem value="loading" disabled>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                        <span className="text-zinc-400 dark:text-zinc-500 italic">Loading subreddits...</span>
                      </div>
                    </SelectItem>
                  ) : approvedSubredditsError ? (
                    <SelectItem value="error" disabled>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                        <span className="text-red-400 italic">Error loading subreddits</span>
                      </div>
                    </SelectItem>
                  ) : approvedSubreddits.length > 0 ? (
                    approvedSubreddits.map((sub) => (
                      <SelectItem 
                        key={sub.subreddit_name || sub.id} 
                        value={sub.subreddit_name || ''} 
                        className="hover:bg-blue-50 dark:hover:bg-blue-950/30 focus:bg-blue-50 dark:focus:bg-blue-950/30 text-zinc-900 dark:text-zinc-100"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                          <span className="font-medium">r/{sub.subreddit_name || 'Unknown'}</span>
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-subreddits" disabled>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-zinc-400"></div>
                        <span className="text-zinc-400 dark:text-zinc-500 italic">No approved subreddits available</span>
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {approvedSubreddits.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 p-2 rounded-md border border-amber-200 dark:border-amber-800/50">
                  {isLoadingApproved 
                    ? "Loading subreddits..." 
                    : approvedSubredditsError 
                      ? `Error loading subreddits: ${approvedSubredditsError.message}. Please refresh the page.`
                      : "⚠️ No approved subreddits found. Please add subreddits in your Reddit settings first."
                  }
                </p>
              )}
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Schedule Date & Time
              </Label>
              <CalendarScheduler
                value={scheduleForm.scheduledAt}
                onChange={(value) => setScheduleForm(prev => ({ ...prev, scheduledAt: value }))}
                existingSchedules={scheduledPosts.map(post => post.scheduled_at_utc)}
              />
            </div>
          </div>
          
          {/* Footer */}
          <div className="bg-zinc-50 dark:bg-zinc-800/50 p-6 flex flex-col sm:flex-row gap-3 border-t border-zinc-200 dark:border-zinc-700 flex-shrink-0">
            <Button 
              variant="outline" 
              onClick={() => setIsScheduleModalOpen(false)}
              className="w-full sm:w-auto border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 h-11"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSchedulePost}
              disabled={!scheduleForm.subreddit || !scheduleForm.scheduledAt || isScheduling || approvedSubreddits.length === 0}
              className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed h-11 font-medium shadow-md"
            >
              {isScheduling ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Scheduling...
                </>
              ) : (
                <>
                  <Calendar className="h-4 w-4 mr-2" />
                  Schedule Post
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RedditContentManager; 